import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { DownloadedUser } from "./projectMessages";
import { AcrScanStatus, AcrScanResult } from "../../types/acr";

// 型定義

// ショットコメントのユーザー情報
export interface ShotCommentUser {
  id: number;
  display_name: string;
  avatar: string | null;
  position?: "CLIENT" | "REVIEWER" | "ADMIN" | "PRODUCER" | "SPECIALIST" | "DIRECTOR";
  seen_at?: string; // 既読日時（ISO 8601形式）
}

// ショットコメントの添付ファイル
export interface ShotCommentFile {
  id: string; // ファイルID
  file_key?: string; // S3キー（更新 API で既存ファイルを保持する際に使用、BE未対応時は original_url から抽出）
  original_url: string; // オリジナルファイルURL
  file_name: string; // ファイル名
  file_type: string; // ファイルタイプ（MIME形式 or カテゴリ形式）
  file_metadata?: string; // ファイルメタデータ（JSON文字列）
  folder?: {
    id: string;
    name: string;
    parent_id: string | null;
  };
  downloaded_users?: DownloadedUser[];
  optimized_url?: string | null; // 最適化済みURL（m4a等）
  waveform_url?: string | null; // 波形JSONファイルURL
  has_waveform?: boolean; // 波形データの有無
  thumbnail?: string | null; // サムネイル画像URL（動画フレーム / PDF 1ページ目）
  acr_scan_status?: AcrScanStatus; // ACR楽曲認識ステータス
  acr_scan_result?: AcrScanResult | null; // ACR楽曲認識結果
  acr_scanned_at?: string | null; // ACR Cloud送信成功日時（processing遷移時にセット）
}

// AI 校正（チクチクフィルター）の元/校正バージョン。投稿者本人にのみ返る。
export interface ShotCommentProofread {
  original: string; // 校正前テキスト
  corrected: string; // AI 校正後テキスト
  active: "original" | "ai"; // 送信時にアクティブだった側
}

export interface ShotComment {
  id: string; // UUID
  comment: string; // コメント本文（常にアクティブ側 = 相手に見える本文）
  is_resolved: boolean; // 解決済みフラグ
  has_files: boolean; // ファイル添付フラグ
  created_at: string; // 作成日時
  updated_at: string; // 更新日時
  author: ShotCommentUser; // ユーザー情報
  shot_id: string | null; // ショットID
  parent_id: string | null; // 親コメントID（返信の場合）
  media_item_id: string | null; // メディアアイテムID
  timecode_start: string; // 旧環境互換: 再生開始位置（"M:SS" / "MM:SS" 形式、空文字 = なし）
  pin_thumbnail_url?: string | null; // ピン時刻の動画フレーム画像URL（タイムコード付きコメントのみ）
  files: ShotCommentFile[]; // 添付ファイル配列
  receivers: ShotCommentUser[]; // 既読ユーザーリスト
  proofread?: ShotCommentProofread | null; // AI 校正の両バージョン（投稿者のみ）
}

export interface ShotCommentsData {
  comments: ShotComment[];
  count: number; // 総数
  has_next: boolean; // 次のページがあるか
  next_offset: number | null; // 次のオフセット
  limit: number; // 取得件数の制限
  offset: number; // 現在のオフセット
  filter: ShotCommentFilterType; // 使用したフィルター
  search?: string | null; // 検索語
}

export interface ShotCommentsResponse {
  status: string;
  data: ShotCommentsData | null;
}

// フィルタータイプ
export type ShotCommentFilterType = 'latest_unresolved' | 'unresolved' | 'all';

export interface GetShotCommentsQueryParams {
  shot_id: string;
  filter?: ShotCommentFilterType;
  limit?: number;
  offset?: number;
  /** true で返信コメントもフラットに含める（parent_id 付き。ショット間移動ウィザード用） */
  include_replies?: boolean;
}

// 単体取得（WS 受信 created/updated で REST 追い取得に利用）
export interface GetShotCommentDetailParams {
  shot_id: string;
  comment_id: string;
}

export interface GetShotCommentDetailResponse {
  status: string;
  data: ShotComment;
}

// AI 校正（チクチクフィルター）の送信用ペイロード（任意）。
// comment には常にアクティブ側本文を入れる前提。original/ai は編集時の復元用。
export interface ShotCommentProofreadPayload {
  original_comment?: string | null;
  ai_comment?: string | null;
  active_variant?: "original" | "ai" | null;
}

// コメント送信用の型定義
export interface SendShotCommentParams extends ShotCommentProofreadPayload {
  shot_id: string;
  comment: string;
  parent_id?: string | null;
  file_keys?: string[];
  media_item_id?: string | null;   // ピン: メディアID
}

// BE は更新時と同じく `data.comment` に作成したコメントを入れて返す。
export interface SendShotCommentResponse {
  status: string;
  data: {
    comment: ShotComment;
  };
}

// コメント更新用の型定義
export interface UpdateShotCommentParams extends ShotCommentProofreadPayload {
  shot_id: string;
  comment_id: string;
  comment?: string;
  is_resolved?: boolean;
  media_item_id?: string | null;
  file_keys?: string[];
}

export interface UpdateShotCommentResponse {
  status: string;
  data: {
    comment: ShotComment;
  };
}

// コメント削除用の型定義
export interface DeleteShotCommentParams {
  shot_id: string;
  comment_id: string;
}

export interface DeleteShotCommentResponse {
  status: string;
  message: string;
}

// ファイルダウンロード記録用の型定義
export interface MarkCommentFilesAsDownloadedParams {
  shot_id: string;
  downloaded_file_ids: string[];
}

export interface MarkCommentFilesAsDownloadedResponse {
  status: string;
  data: {
    downloaded_file_ids: string[];
  };
}

/**
 * ショットコメント一括既読（トークルームの project_messages PATCH + message_ids と対称）
 * PATCH /api/{shot_id}/comments ボディに comment_ids を付与。
 */
export interface MarkShotCommentsAsReadParams {
  shot_id: string;
  comment_ids: string[];
}

export interface MarkShotCommentsAsReadResponse {
  status: string;
  data?: {
    read_comment_ids?: string[];
  };
}

// APIサービス定義
export const shotCommentsApi = createApi({
  reducerPath: "shotCommentsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  endpoints: (builder) => ({
    getShotComments: builder.query<ShotCommentsResponse, GetShotCommentsQueryParams>({
      query: ({ shot_id, filter = 'all', limit = 20, offset = 0, include_replies }) => {
        const params = new URLSearchParams({
          filter,
          limit: limit.toString(),
          offset: offset.toString(),
        });
        if (include_replies) {
          params.set('include_replies', 'true');
        }
        return `/api/${shot_id}/comments?${params.toString()}`;
      },
      keepUnusedDataFor: 0, // Invalidate cache immediately
    }),
    // 単体取得（WebSocket created/updated 受信時に差分を追い取得する用途）
    getShotCommentDetail: builder.query<
      GetShotCommentDetailResponse,
      GetShotCommentDetailParams
    >({
      query: ({ shot_id, comment_id }) =>
        `/api/${shot_id}/comments/${comment_id}`,
      keepUnusedDataFor: 0,
    }),
    // コメント送信用のmutation
    sendShotComment: builder.mutation<SendShotCommentResponse, SendShotCommentParams>({
      query: ({ shot_id, comment, parent_id, file_keys, media_item_id, original_comment, ai_comment, active_variant }) => ({
        url: `/api/${shot_id}/comments`,
        method: "POST",
        body: {
          comment,
          ...(parent_id && { parent_id }),
          ...(file_keys && file_keys.length > 0 && { file_keys }),
          ...(media_item_id && { media_item_id }),
          ...(original_comment !== undefined && { original_comment }),
          ...(ai_comment !== undefined && { ai_comment }),
          ...(active_variant !== undefined && { active_variant }),
        },
      }),
    }),
    // コメント更新用のmutation
    updateShotComment: builder.mutation<UpdateShotCommentResponse, UpdateShotCommentParams>({
      query: ({ shot_id, comment_id, comment, is_resolved, media_item_id, file_keys, original_comment, ai_comment, active_variant }) => ({
        url: `/api/${shot_id}/comments/${comment_id}`,
        method: "PATCH",
        body: {
          ...(comment !== undefined && { comment }),
          ...(is_resolved !== undefined && { is_resolved }),
          ...(media_item_id !== undefined && { media_item_id }),
          ...(file_keys !== undefined && { file_keys }),
          ...(original_comment !== undefined && { original_comment }),
          ...(ai_comment !== undefined && { ai_comment }),
          ...(active_variant !== undefined && { active_variant }),
        },
      }),
    }),
    // コメント削除用のmutation
    deleteShotComment: builder.mutation<DeleteShotCommentResponse, DeleteShotCommentParams>({
      query: ({ shot_id, comment_id }) => ({
        url: `/api/${shot_id}/comments/${comment_id}`,
        method: "DELETE",
      }),
    }),
    // ファイルダウンロード記録用mutation
    markCommentFilesAsDownloaded: builder.mutation<MarkCommentFilesAsDownloadedResponse, MarkCommentFilesAsDownloadedParams>({
      query: ({ shot_id, downloaded_file_ids }) => ({
        url: `/api/${shot_id}/comments`,
        method: "PATCH",
        body: { downloaded_file_ids },
      }),
    }),
    // 複数コメントの既読登録（トークルームの markMessagesAsRead と同様）
    markShotCommentsAsRead: builder.mutation<MarkShotCommentsAsReadResponse, MarkShotCommentsAsReadParams>({
      query: ({ shot_id, comment_ids }) => ({
        url: `/api/${shot_id}/comments`,
        method: "PATCH",
        body: { comment_ids },
      }),
    }),
  }),
});

// コンポーネント用hooksのエクスポート
export const {
  useGetShotCommentsQuery,
  useLazyGetShotCommentsQuery,
  useGetShotCommentDetailQuery,
  useLazyGetShotCommentDetailQuery,
  useSendShotCommentMutation,
  useUpdateShotCommentMutation,
  useDeleteShotCommentMutation,
  useMarkCommentFilesAsDownloadedMutation,
  useMarkShotCommentsAsReadMutation,
} = shotCommentsApi;

